import React from "react";
import { useQuery } from "@tanstack/react-query";
import { TrendingUp, User, ChevronsUpDown } from "lucide-react";
import { api, formatRank } from "@/lib/api";
import { T } from "@/lib/testIds";
import { cn } from "@/lib/utils";

const formatPoints = (v) => (v === null || v === undefined ? "—" : v.toLocaleString());
const formatRankLabel = (n) => {
  if (n === 1) return "Rank 1";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 ? 1 : 0)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 ? 1 : 0)}K`;
  return `Rank ${n}`;
};

export default function RankGap() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["rank-gap"],
    queryFn: async () => (await api.get("/rank-gap")).data,
    staleTime: 5 * 60_000,
  });

  if (isLoading) return <div className="totem-card h-96 animate-pulse mt-4" />;
  if (error) return <div className="totem-card p-6 text-totem-red">{String(error.message)}</div>;

  const user = data.user;
  const checkpoints = data.checkpoints || [];
  const anyPop = data.any_populated;

  // Compose rows: checkpoints in rank ascending + user row inserted at correct position.
  const rows = [];
  const userPts = user.points || 0;
  let userInserted = false;

  const sorted = [...checkpoints].sort((a, b) => a.rank - b.rank);

  const pushUser = () => {
    rows.push({ isUser: true, ...user });
    userInserted = true;
  };

  for (const cp of sorted) {
    // If cp is populated and user has better points → user goes ABOVE this cp (user rank < cp.rank)
    if (
      !userInserted &&
      cp.points_at_rank !== null &&
      userPts >= cp.points_at_rank
    ) {
      pushUser();
    }
    rows.push({ isUser: false, ...cp });
  }
  if (!userInserted) pushUser();

  return (
    <div data-testid={T.rankGapPage} className="space-y-4 pt-1 animate-fade-up">
      <div className="flex items-center gap-2">
        <TrendingUp className="text-totem-green" size={18} />
        <h2 className="display-heading text-lg">Rank Gap Benchmarks</h2>
      </div>

      <section className="totem-card p-5">
        <div className="label-caps mb-2">You</div>
        <div className="flex items-end justify-between">
          <div>
            <div className="display-heading text-2xl">{user.team_name || user.player_name || "—"}</div>
            <div className="mono text-xs text-totem-textDim uppercase mt-1">
              Manager #{user.manager_id}
            </div>
          </div>
          <div className="text-right">
            <div className="display-heading text-3xl text-totem-green">
              {formatPoints(user.points)}
            </div>
            <div className="mono text-[10px] uppercase text-totem-textDim mt-1">
              Overall {formatRank(user.rank)}
            </div>
          </div>
        </div>
      </section>

      {!anyPop && (
        <section className="totem-card p-6 text-center border-totem-amber/30">
          <ChevronsUpDown className="mx-auto text-totem-amber mb-3" size={28} />
          <div className="display-heading text-lg mb-2">Pre-season</div>
          <p className="text-sm text-totem-textDim leading-relaxed">
            Rank-gap benchmarks appear once GW1 finalises and the Overall league
            starts populating. Check back after the first deadline.
          </p>
        </section>
      )}

      {anyPop && (
        <section className="totem-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-totem-surface">
              <tr>
                <th className="text-left px-3 py-2.5 label-caps">Tier</th>
                <th className="text-left px-3 py-2.5 label-caps">Manager</th>
                <th className="text-right px-3 py-2.5 label-caps">Pts</th>
                <th className="text-right px-3 py-2.5 label-caps">Gap</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                if (r.isUser) {
                  return (
                    <tr
                      key={`user-${i}`}
                      data-testid={T.rankGapUserRow}
                      className="bg-totem-green/10 border-t border-totem-green/40"
                    >
                      <td className="px-3 py-3">
                        <div className="mono uppercase text-[10px] text-totem-green font-semibold">
                          You
                        </div>
                        <div className="mono text-xs text-totem-textDim">
                          {r.rank ? formatRank(r.rank) : "—"}
                        </div>
                      </td>
                      <td className="px-3 py-3 min-w-0">
                        <div className="font-semibold text-totem-green truncate flex items-center gap-1">
                          <User size={12} /> {r.team_name || r.player_name || "You"}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right mono font-semibold text-totem-green">
                        {formatPoints(r.points)}
                      </td>
                      <td className="px-3 py-3 text-right mono text-totem-textDim">—</td>
                    </tr>
                  );
                }
                const gap =
                  r.points_at_rank !== null
                    ? (user.points || 0) - r.points_at_rank
                    : null;
                const behind = gap !== null && gap < 0;
                const ahead = gap !== null && gap >= 0;

                return (
                  <tr
                    key={`cp-${r.rank}`}
                    data-testid={T.rankGapRow}
                    className="border-t border-totem-borderSoft"
                  >
                    <td className="px-3 py-3">
                      <div className="mono uppercase text-[11px] font-semibold">
                        {formatRankLabel(r.rank)}
                      </div>
                    </td>
                    <td className="px-3 py-3 min-w-0">
                      {r.unavailable ? (
                        <span className="text-totem-textFaint mono text-[10px] uppercase">
                          Not available yet
                        </span>
                      ) : (
                        <>
                          <div className="font-medium truncate">
                            {r.team_name || "—"}
                          </div>
                          <div className="mono text-[10px] uppercase text-totem-textDim truncate">
                            {r.manager_name || ""}
                          </div>
                        </>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right mono">
                      {formatPoints(r.points_at_rank)}
                    </td>
                    <td
                      className={cn(
                        "px-3 py-3 text-right mono",
                        ahead && "text-totem-green",
                        behind && "text-totem-amber"
                      )}
                    >
                      {gap === null ? "—" : gap >= 0 ? `+${gap}` : gap}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      <p className="text-[11px] text-totem-textDim leading-relaxed px-1">
        Checkpoints pulled from the Overall league (id 314) and cached for 6 hours.
        Deeper tiers (1M, 4M) require paginating tens of thousands of pages and are
        deferred to a scheduled job in a later phase.
      </p>
    </div>
  );
}
